import { Controller } from "react-hook-form";
import {
  Stack,
  Textarea,
  Group,
  Text,
  Divider,
  ThemeIcon,
  Center,
} from "@mantine/core";
import { useMemo } from "react";
import { HiExclamationTriangle } from "react-icons/hi2";
import { buildGroupedPermissionOptions } from "../../../utils/BuildGroupedPermissions";
import { CustomSwitch } from "../../ui/CustomSwitch/CustomSwitch";
import CustomModal from "../../ui/CustomModal/CustomModal";
import CustomTextInput from "../../ui/CustomTextInput/CustomTextInput";
import { CustomText } from "../../ui/CustomText/CustomText";
import CustomButton from "../../ui/CustomButton/CustomButton";
import CustomLoader from "../../ui/CustomLoader/CustomLoader";
import CustomMultiSelect from "../../ui/CustomMultiSelect/CustomMultiSelect";
import FetchFeedback from "../../ui/CustomFetchFeedback/CustomFetchFeedback";
import {
  UserValidators,
  RoleValidators,
  PermissionValidators,
} from "../../../lib/validations/RbacValidations";

export const PermissionModal = ({
  opened,
  onClose,
  onSubmit,
  control,
  isSubmitting,
  mode = "create",
}) => {
  const isEdit = mode === "edit";
  const title = isEdit ? "Edit Permission" : "Add Permission";
  const buttonLabel = isEdit ? "Update" : "Create";

  return (
    <CustomModal
      opened={opened}
      onClose={onClose}
      title={
        <CustomText
          fw={600}
          size="lg"
          style={{ textAlign: "center", color: "#1c7ed6" }}
        >
          {title}
        </CustomText>
      }
      centered
      withCloseButton={!isSubmitting}
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
      overlayProps={{ backgroundOpacity: 0.5, blur: 2 }}
      radius="md"
      size="md"
      styles={{ body: { padding: 0 } }}
    >
      <div
        style={{
          maxHeight: "calc(90dvh - 160px)",
          overflowY: "auto",
          padding: "0 1rem 1rem",
        }}
      >
        <Divider mb="md" />

        <form onSubmit={onSubmit}>
          <Stack gap="md">
            <Controller
              name="name"
              control={control}
              rules={{ validate: (v) => PermissionValidators.name(v) ?? true }}
              render={({ field, fieldState }) => (
                <CustomTextInput
                  label={
                    <CustomText component="span" fw={700} size="sm">
                      Permission Name
                    </CustomText>
                  }
                  placeholder="Enter permission name"
                  required={!isEdit}
                  withAsterisk={!isEdit}
                  readOnly={isEdit && isSubmitting}
                  disabled={isSubmitting}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              rules={{
                validate: (v) => PermissionValidators.description(v) ?? true,
              }}
              render={({ field, fieldState }) => (
                <Textarea
                  label={
                    <Text component="span" fw={700} size="sm">
                      Description
                    </Text>
                  }
                  placeholder="Enter description"
                  autosize
                  minRows={2}
                  required={!isEdit}
                  withAsterisk={!isEdit}
                  readOnly={isEdit && isSubmitting}
                  disabled={isSubmitting}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                />
              )}
            />

            <Group justify="flex-end" mt="md">
              <CustomButton
                variant="default"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </CustomButton>
              <CustomButton
                type="submit"
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                {buttonLabel}
              </CustomButton>
            </Group>
          </Stack>
        </form>
      </div>
    </CustomModal>
  );
};

export const ConfirmModal = ({
  opened,
  onConfirm,
  onCancel,
  title = "Confirm",
  message = "Are you sure?",
  isLoading = false,
  confirmLabel = "Yes",
  cancelLabel = "No",
  icon = <HiExclamationTriangle style={{ fontSize: 18 }} />,
  iconColor = "red",
  confirmColor = "error",
  size = "md",
}) => {
  return (
    <CustomModal
      opened={opened}
      onClose={onCancel}
      title={
        <CustomText fw={600} size="md">
          {title}
        </CustomText>
      }
      centered
      withCloseButton={!isLoading}
      closeOnClickOutside={!isLoading}
      closeOnEscape={!isLoading}
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      radius="md"
      size={size}
    >
      <Stack spacing="md">
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <ThemeIcon color={iconColor} radius="xl" size="xl">
            {icon}
          </ThemeIcon>
          <Text size="sm" c="dimmed" style={{ flex: 1 }}>
            {message}
          </Text>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <CustomButton
            variant="default"
            onClick={onCancel}
            disabled={isLoading}
          >
            {cancelLabel}
          </CustomButton>
          <CustomButton
            color={confirmColor}
            onClick={onConfirm}
            loading={isLoading}
            disabled={isLoading}
          >
            {confirmLabel}
          </CustomButton>
        </div>
      </Stack>
    </CustomModal>
  );
};

export const RoleModal = ({
  opened,
  onClose,
  onSubmit,
  control,
  permissions = [],
  loadingPermissions = false,
  isPermissionsError = false,
  isSubmitting = false,
  mode = "create",
  refetchPermissions = () => {},
}) => {
  const isEdit = mode === "edit";
  const title = isEdit ? "Edit Role" : "Add Role";
  const buttonLabel = isEdit ? "Update" : "Create";
  const hasPermissions = Array.isArray(permissions) && permissions.length > 0;
  const shouldShowLoader = loadingPermissions && !isPermissionsError;
  const shouldShowError =
    isPermissionsError || (!loadingPermissions && !hasPermissions);

  const groupedPermissions = useMemo(
    () => buildGroupedPermissionOptions(permissions),
    [permissions],
  );

  return (
    <CustomModal
      opened={opened}
      onClose={onClose}
      title={
        <CustomText
          fw={600}
          size="lg"
          style={{ textAlign: "center", color: "#1c7ed6" }}
        >
          {title}
        </CustomText>
      }
      centered
      withCloseButton={!isSubmitting}
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
      overlayProps={{ backgroundOpacity: 0.5, blur: 2 }}
      radius="md"
      size="lg"
      styles={{ body: { padding: 0 } }}
    >
      <div
        style={{
          minHeight: "400px",
          maxHeight: "calc(90dvh - 160px)",
          overflowY: "auto",
          padding: "0 1rem 1rem",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Divider mb="md" />

        <form
          onSubmit={onSubmit}
          style={{ flex: 1, display: "flex", flexDirection: "column" }}
        >
          <Stack gap="md" style={{ flex: 1 }}>
            <Controller
              name="name"
              control={control}
              rules={{ validate: (v) => RoleValidators.name(v) ?? true }}
              render={({ field, fieldState }) => (
                <CustomTextInput
                  label={
                    <CustomText component="span" fw={700} size="sm">
                      Role Name
                    </CustomText>
                  }
                  placeholder="Enter role name"
                  required={!isEdit}
                  withAsterisk={!isEdit}
                  readOnly={isEdit && isSubmitting}
                  disabled={isSubmitting}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                />
              )}
            />

            <div>
              {shouldShowLoader && (
                <Center h={120}>
                  <Stack align="center" gap="xs">
                    <CustomLoader
                      color="primary"
                      text="Loading Permissions..."
                    />
                  </Stack>
                </Center>
              )}

              {shouldShowError && (
                <FetchFeedback
                  fullHeight={false}
                  message="Failed To Fetch Permissions"
                  onRetry={refetchPermissions}
                />
              )}

              {!shouldShowLoader && !shouldShowError && hasPermissions && (
                <Controller
                  name="permissionIds"
                  control={control}
                  rules={{
                    validate: (v) => RoleValidators.permissionIds(v) ?? true,
                  }}
                  render={({ field, fieldState }) => (
                    <CustomMultiSelect
                      label={
                        <CustomText component="span" fw={700} size="sm">
                          Permissions
                        </CustomText>
                      }
                      placeholder="Select permissions"
                      data={groupedPermissions}
                      searchable
                      comboboxProps={{
                        withinPortal: false,
                      }}
                      required
                      disabled={isSubmitting}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              )}
            </div>

            <Group justify="flex-end" mt="auto">
              <CustomButton
                variant="default"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </CustomButton>
              <CustomButton
                type="submit"
                disabled={isSubmitting || isPermissionsError || !hasPermissions}
                loading={isSubmitting}
              >
                {buttonLabel}
              </CustomButton>
            </Group>
          </Stack>
        </form>
      </div>
    </CustomModal>
  );
};

export const MerchantUserModal = ({
  opened,
  onClose,
  onSubmit,
  control,
  isSubmitting,
  mode = "create",
}) => {
  const isEdit = mode === "edit";
  const title = isEdit ? "Edit User" : "Add User";
  const buttonLabel = isEdit ? "Update" : "Create";

  return (
    <CustomModal
      opened={opened}
      onClose={onClose}
      title={
        <Text
          fw={600}
          size="lg"
          style={{ textAlign: "center", color: "#1c7ed6" }}
        >
          {title}
        </Text>
      }
      overlayProps={{
        backgroundOpacity: 0.5,
        color: "#282828",
        blur: 1,
      }}
      centered
      radius="md"
      size="auto"
      withCloseButton={!isSubmitting}
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
      styles={{
        content: {
          borderRadius: "10px",
          padding: "2rem",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
          maxWidth: "480px",
          width: "100%",
        },
        body: { padding: 0 },
      }}
    >
      <div style={{ maxHeight: "calc(90dvh - 160px)", overflowY: "auto" }}>
        <Divider mb="md" />
        <form onSubmit={onSubmit}>
          <Stack gap="md">
            <Controller
              name="firstname"
              control={control}
              rules={{ validate: (v) => UserValidators.firstname(v) ?? true }}
              render={({ field, fieldState }) => (
                <CustomTextInput
                  label={
                    <Text component="span" fw={700} size="sm">
                      First Name
                    </Text>
                  }
                  placeholder="Enter first name"
                  required={!isEdit}
                  withAsterisk={!isEdit}
                  readOnly={isEdit && isSubmitting}
                  disabled={isSubmitting}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                />
              )}
            />

            <Controller
              name="lastname"
              control={control}
              rules={{ validate: (v) => UserValidators.lastname(v) ?? true }}
              render={({ field, fieldState }) => (
                <CustomTextInput
                  label={
                    <Text component="span" fw={700} size="sm">
                      Last Name
                    </Text>
                  }
                  placeholder="Enter last name"
                  required={!isEdit}
                  withAsterisk={!isEdit}
                  readOnly={isEdit && isSubmitting}
                  disabled={isSubmitting}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                />
              )}
            />

            <Controller
              name="email"
              control={control}
              rules={{ validate: (v) => UserValidators.email(v) ?? true }}
              render={({ field, fieldState }) => (
                <CustomTextInput
                  label={
                    <Text component="span" fw={700} size="sm">
                      Email
                    </Text>
                  }
                  placeholder="Enter email"
                  required={!isEdit}
                  withAsterisk={!isEdit}
                  readOnly={isEdit && isSubmitting}
                  disabled={isSubmitting}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                />
              )}
            />

            <Controller
              name="msisdn"
              control={control}
              rules={{ validate: (v) => UserValidators.msisdn(v) ?? true }}
              render={({ field, fieldState }) => (
                <CustomTextInput
                  label={
                    <Text component="span" fw={700} size="sm">
                      Phone Number(MSISDN)
                    </Text>
                  }
                  placeholder="Enter phone number"
                  required={!isEdit}
                  withAsterisk={!isEdit}
                  readOnly={isEdit && isSubmitting}
                  disabled={isSubmitting}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                />
              )}
            />

            <CustomText fw={700} size="sm" mb={4}>
              Two-Factor Authentication (MFA)
            </CustomText>
            <Controller
              name="mfaenabled"
              control={control}
              render={({ field }) => (
                <CustomSwitch
                  checked={field.value ?? true}
                  onChange={(e) => field.onChange(e.currentTarget.checked)}
                  activeLabel="Enabled"
                  inactiveLabel="Disabled"
                  disabled={true}
                />
              )}
            />
            <CustomText size="xs" c="dimmed" mt={4}>
              MFA is always enabled to ensure account security.
            </CustomText>

            <Group justify="flex-end" mt="md">
              <CustomButton
                variant="default"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </CustomButton>
              <CustomButton
                type="submit"
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                {buttonLabel}
              </CustomButton>
            </Group>
          </Stack>
        </form>
      </div>
    </CustomModal>
  );
};

export const ManageRoleModal = ({
  opened,
  onClose,
  onSubmit,
  userName = "",
  control,
  roles = [],
  loadingRoles = false,
  isrolesError = false,
  isSubmitting = false,
  refetchRoles = () => {},
}) => {
  const hasRoles = Array.isArray(roles) && roles.length > 0;
  const shouldShowLoader = loadingRoles && !isrolesError;
  const shouldShowError = isrolesError || (!loadingRoles && !hasRoles);

  return (
    <CustomModal
      opened={opened}
      onClose={onClose}
      title={
        <Text
          fw={600}
          size="lg"
          style={{ textAlign: "center", color: "#1c7ed6" }}
        >
          Manage Roles for {userName}
        </Text>
      }
      centered
      withCloseButton={!isSubmitting}
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
      overlayProps={{ backgroundOpacity: 0.5, blur: 2 }}
      radius="md"
      size="lg"
      styles={{ body: { padding: 0 } }}
    >
      <div
        style={{
          minHeight: "200px",
          maxHeight: "calc(90dvh - 160px)",
          overflowY: "auto",
          padding: "0 1rem 1rem",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Divider mb="md" />
        <form
          onSubmit={onSubmit}
          style={{ flex: 1, display: "flex", flexDirection: "column" }}
        >
          <Stack gap="md" style={{ flex: 1 }}>
            <div>
              {shouldShowLoader && (
                <Center h={120}>
                  <Stack align="center" gap="xs">
                    <CustomLoader color="primary" text="Loading roles..." />
                  </Stack>
                </Center>
              )}

              {shouldShowError && (
                <FetchFeedback
                  fullHeight={false}
                  message="Failed To Fetch Roles"
                  onRetry={refetchRoles}
                />
              )}

              {!shouldShowLoader && !shouldShowError && roles?.length > 0 && (
                <Controller
                  name="roleIds"
                  control={control}
                  render={({ field }) => (
                    <CustomMultiSelect
                      label={
                        <CustomText component="span" fw={700} size="sm">
                          Roles
                        </CustomText>
                      }
                      placeholder="Select roles"
                      data={roles.map((role) => ({
                        value: role.id,
                        label: role.name,
                      }))}
                      searchable
                      comboboxProps={{
                        withinPortal: false,
                      }}
                      disabled={isSubmitting}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    />
                  )}
                />
              )}
            </div>

            <Group justify="flex-end" mt="auto">
              <CustomButton
                variant="default"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </CustomButton>
              <CustomButton
                type="submit"
                disabled={isSubmitting || isrolesError}
                loading={isSubmitting}
              >
                Update Roles
              </CustomButton>
            </Group>
          </Stack>
        </form>
      </div>
    </CustomModal>
  );
};
